import type { Ceviri } from "../tip";
import type { yedek as tr } from "../tr/yedek";

/** English dictionary · yedek. */
export const yedek: Ceviri<typeof tr> = {
  hedefAdi: {
    yerelKlasor: "Synced folder",
    parafonSunucu: "Parafon server",
  },

  hedef: {
    yapilandirilmamis: "Not configured on this server (no app registration — deploy/OKUBENI.md).",
    adYapilandirilmamis: (ad: string, sebep: string) => `${ad}: ${sebep}`,
    merkezYok: "Connection to the Parafon server is off in this session.",
    sunucuNot: (enAz: number, ay: number) =>
      `No account needed: encrypted, with a vault code. The vault password must be at least ${enAz} characters. A folder not opened from any device for ${ay} months is deleted.`,
    dropboxNot: "Writes to the app folder. Once connected, it keeps going by itself.",
    googleNot:
      "Writes to the hidden app folder. Google does not give browser apps long-lasting access: once an hour, if a backup is waiting, it asks for one click.",
    onedriveNot:
      "Writes to the app folder. Microsoft limits browser app access to 24 hours: once a day, it asks for one click.",
    klasorNot:
      "Choose the Google Drive / Dropbox / iCloud / OneDrive folder on your computer; the desktop client carries it to the cloud. The browser may ask for permission once after it reopens.",
    sunucuSecin: "New vault code or an existing code — choose one.",
    icloudNotu:
      "On iPhone, the browser cannot write to iCloud directly (it needs Apple's paid developer membership). On a computer (Mac or Windows, Chrome or Edge), choose the iCloud Drive folder; iCloud syncs it itself.",

    klasorBrave:
      "This feature is off in Brave: type brave://flags/#file-system-access-api in the address bar → Enabled → restart the browser.",
    klasorMobil: "Not available in mobile browsers (desktop Chrome or Edge needed).",
    klasorFirefox: "Firefox does not support this feature (desktop Chrome or Edge needed).",
    klasorSafari: "Safari does not support this feature (desktop Chrome or Edge needed).",
    klasorYok: "Not available in this browser (desktop Chrome or Edge needed).",
    klasorSecilemiyor: "This browser does not support choosing a folder (desktop Chrome or Edge needed).",
    klasorIzinYok: "Permission to write to the folder was not given.",
    klasorIzinGeriAlindi: "Folder permission was withdrawn — reconnect.",
    klasorKayboldu: "The chosen folder no longer exists (it may have been moved or deleted). Choose a new folder.",
    klasorKonum: (ad: string) => `Folder: ${ad}`,

    googleAnahtarYok: "Google did not return an access token.",
    googleIzinYok: "Google did not give access to the app folder.",
    googleSureDoldu: "Google access expired — reconnect.",
    googleReddetti: "Google refused access — reconnect.",
    googleKonum: "Google Drive › app data (hidden folder)",
    googleDosyaYok: (ad: string) => `${ad} not found in Google Drive.`,

    dropboxKodYok: "Dropbox did not return an authorisation code.",
    dropboxYenilemeYok: "Dropbox did not give a refresh token (is 'offline' access enabled in the app registration?).",
    dropboxGeriAlindi: "Dropbox connection was withdrawn — reconnect.",
    dropboxReddetti: "Dropbox refused access — reconnect.",
    dropboxKonum: "Dropbox › Apps › (app registration name)",

    onedriveKodYok: "OneDrive did not return an authorisation code.",
    onedriveAnahtarYok: "OneDrive did not give an access token.",
    onedriveGunlukDoldu: "The daily OneDrive session expired — reconnect.",
    onedriveDustu: "OneDrive connection dropped — reconnect.",
    onedriveReddetti: "OneDrive refused access — reconnect.",
    onedriveKonum: "OneDrive › Apps › (app registration name)",
    onedriveIndirmeAdresiYok: (ad: string) => `OneDrive did not give a download address for ${ad}.`,
    onedriveIndirme: (durum: number) => `OneDrive download ${durum}`,

    kodTaninmadi:
      "Vault code not found on the server: it may have been renewed on another device, or deleted because no device opened it for 7 months. Enter the new code.",
    sunucuCokIstek: "The server received too many requests from this connection in a short time; try again a little later.",
    sunucuDolu: "The Parafon server's backup space is full right now; a new vault code cannot be opened. Choose another destination.",
    klasorSinir: "This vault code's folder has reached its limit (number of files or total size).",
    dosyaBuyuk: "The backup file exceeds the server's single-file limit.",
    govdeBuyuk: "The backup file exceeds the size the server accepts; the server configuration needs updating.",
    kodBicimi: (n: number) => `A vault code has ${n} letters and digits; the code you typed is incomplete or wrong.`,
    kodYok: "This vault code does not exist on the server. Check the code in Settings on the other device that uses the vault.",
    kodAcilamadi: "A new vault code could not be opened.",
    sunucuKonum: (ay: number) =>
      `Parafon server › your vault code's folder (deleted if not opened from any device for ${ay} months)`,
    sunucuYenidenBaglanmaz: "The server backup does not reconnect: if the vault code changed, enter the new code.",
  },

  oauth: {
    pencereEngellendi: "Pop-up blocked. The browser needs to allow pop-ups for this site.",
    iptal: "Connection cancelled.",
    zamanAsimi: "Connection timed out (5 minutes).",
    yetkiYok: (sebep: string) => `The provider did not authorise: ${sebep}`,
    belirtecYok: (durum: number, sebep: string) => `Token could not be obtained (${durum}): ${sebep}`,
  },

  kayit: {
    idbIstek: "IndexedDB request failed",
    idbYokKurulamaz: "This browser has no IndexedDB — automatic backup cannot be set up.",
    idbIslem: "IndexedDB transaction failed",
    idbIptal: "IndexedDB transaction aborted",
    idbYok: "This browser has no IndexedDB.",
    idbAcilamadi: "IndexedDB could not be opened",
    idbDustu: "IndexedDB transaction dropped",
    kasaBos: "The vault is empty — an empty vault is not written to the cloud (so it does not overwrite the last cloud backup).",
    anahtarYok: "The backup record has no key — it cannot be written without a locked vault.",
    kuruluDegil: "Automatic backup is not set up.",
    modulUyumsuz:
      "The backup module loaded but does not have the expected functions — the web bundle and code version may be out of step.",
  },

  saglayici: {
    parolaGerekli: "Backup password required.",
    eskiYedekParolayla: "An old backup opens with a password.",
    degistirmeKilitli: "Replacing the vault is only possible with an encrypted (locked) vault.",
    sunucuDegil: "The configured destination is not the Parafon server.",
    anahtarYok: "No backup key — set up automatic backup again first.",
  },

  qr: {
    sigmiyor: "The content does not fit in a QR code; use the link or the file.",
    kameraYok: "This browser cannot access the camera; type the code by hand.",
    kameraIzni: "Camera permission was not given. Type the code by hand, or allow the camera in the browser's site settings.",
    kameraBulunamadi: "No usable camera found on this device; type the code by hand.",
    kameraAcilamadi: "The camera could not be opened; type the code by hand.",
    kodQr: "Vault code QR",
    hazirlaniyor: "Preparing QR…",
    okut: "Scan QR",
    kameraGoruntusu: "Camera view",
    cerceveyeTutun:
      "Hold the vault code QR from the other device in the frame. The image is processed on this device and sent nowhere.",
    yabanci: "The scanned QR is not a vault code; still searching.",
    kameraKapat: "Close camera",
  },

  bant: {
    cakisma: (n: number) =>
      `Sync conflict in ${n} ${n === 1 ? "record" : "records"}: both devices had changed it, the later change was kept.`,
    bekliyorBuluta: "Cloud backup waiting: reconnect to the cloud to write.",
    bekliyorHedef: (ad: string) => `Cloud backup waiting: reconnect to ${ad} to write.`,
    yazilamadi: (mesaj: string) => `The cloud copy could not be written: ${mesaj} ·`,
    ayarlar: "Settings",
    yalnizcaTarayicida: "Your vault is only in this browser — no cloud storage chosen.",
    ayarlarDepolama: "Settings › Storage",
    indirilmemis: "The vault has changes whose backup has not been downloaded.",
    ayarDegisti: "Settings have changed. Download the backup?",
    yedegiIndir: "Download backup",
    indiMi: (dosya: string) => `Did the backup file (${dosya}) download? Check your downloads folder.`,
    indi: "It did",
    inmedi: "It didn't",
  },

  otoIndir: {
    baslik: "Download automatically on change",
    aciklama:
      "While no cloud storage is connected, an encrypted backup file (.pfai) is downloaded to this device when the vault changes. The file opens with the vault password or the 24-word recovery phrase.",
    bulutBagli:
      "Cloud storage is connected: every change is already written to the cloud, so automatic download is off. It can be turned on again once the cloud is removed.",
    bekleme: "Wait after a change",
    saniye: (n: number) => `${n} s`,
    dakika: (n: number) => `${n} min`,
    uyariBaslik: "Checking the downloads is your responsibility",
    uyari1:
      "Automatic download depends on the browser's download permission. If the browser blocks a download, holds it for confirmation or cannot save the file, Parafon cannot know: download failures may not be reported to you.",
    uyari2:
      "It is your responsibility to check regularly that the files actually reached your device and to keep them off this device. If this device is lost or the browser data is cleared, your vault comes back only from these files.",
    sonIndirme: (dosya: string, zaman: string) => `Last download: ${dosya} · ${zaman}`,
    sonIndirmeYok: "Not downloaded yet.",
    simdiIndir: "Download now",
    izinYok:
      "You said the file did not download in this browser: automatic download is paused on this device, and a warning appears at the top after changes.",
    yenidenDene: "Try again",
    geriYukleme:
      "To restore: on a new device, “Restore from cloud” › “from a backup file on this device” on the setup screen; in this vault, Settings › Encrypted backup › Restore from backup.",
  },

  soru: {
    baslik: "Where should your vault be kept?",
    sonradan: "You can change this later in Settings › Storage.",
    uyariAdi: "No storage chosen",
    neYapmali: "Choose in Settings › Storage whether the vault stays only on this device or also in the cloud.",
    serit: (ad: string) => `${ad}: where should your vault be kept?`,
  },

  depolama: {
    cakismaTuru: {
      account: "Account",
      transaction: "Transaction",
      manualValuation: "Manual valuation",
      manualPrice: "Manual price",
      setting: "Setting",
    },
    bulutSatiri:
      "The vault also stays on this device; in the cloud it has an encrypted copy the provider cannot open, updated with every change.",
    baslik: "Storage",
    okunuyor: "Reading storage status…",
    geriYuklemeTamam: "Restore from cloud complete",
    kilidiKullaniyor: "This device now uses that vault's lock",
    anahtarBenimsendi:
      "The PASSWORD and 24 WORDS of the vault the backup came from are now valid on this device too. The words shown for this device during setup no longer work.",
    koddaKasaYok: "There is no vault under this code yet. For a fresh start, choose “Create new vault code”.",
    yedekParolasiGerekli: "Backup password required (the new copy is encrypted with this password).",
    kasaNeredeDursun: "Where should the vault be kept",
    yalnizcaCihaz: "Only on this device",
    yalnizcaCihazAciklama:
      "If the browser's site data is cleared or the device is lost, the vault is gone. You can save a file by hand with “Encrypted backup (.pfai)” below.",
    bulutDepolama: "Cloud storage",
    yalnizcaCihazKalsin: "Keep it only on this device",
    baskaCihazdaKullandiysaniz:
      "If you used Parafon on another device, choose the place you chose there: your vault comes from there.",
    yedekParolasi: "Backup password",
    yedekParolasiIpucu:
      "Asked once. Needed to restore on another device or after site data is cleared; if lost, the backup cannot be opened.",
    parolaTekrar: "Password (again)",
    buluttaDosyaVar: (n: number) => `This cloud has ${n} backup ${n === 1 ? "file" : "files"}`,
    kasaBos:
      "The vault on this device is empty right now. To bring your vault from another device, choose the file and enter that vault's password; this device then syncs to the same place.",
    buCihazdakiyleBasla: "Start with this device's vault",

    baglan: "Connect",
    bulutSaglayicisi: "Cloud provider",
    saglayiciSecin: "Choose a provider.",
    ikiParolaAyniDegil: "The two passwords do not match.",
    saglayiciYok: "No cloud provider is available in this browser right now",
    saglayiciYokMetin:
      "Each option says why it is unavailable. Until then, take a backup by hand with “Encrypted backup (.pfai)”.",
    baglaniyor: "Connecting…",

    sunucuKopyasiIpucu: "Does nothing on its own: applied once you choose a place below and confirm.",
    yalnizcaCihazSecenegi: "Copies stop being written to the cloud. If automatic download on change is on, the encrypted backup is downloaded to this device.",
    sunucuKopyasiSilinsin: "Delete the encrypted copy on the Parafon server",
    nerede: "Where",
    sonYedek: "Last backup",
    henuzYok: "none yet",
    oburCihazlardan: "From other devices",
    henuzBakilmadi: "not checked yet",
    baskaCihazYok: (zaman: string) => `no other device uses this vault · ${zaman}`,
    degisiklikAlindi: (n: number, zaman: string) => `${n} ${n === 1 ? "change" : "changes"} received · ${zaman}`,
    cakismaBaslik: (n: number) => `Conflict in ${n} ${n === 1 ? "record" : "records"} at the last sync`,
    cakismaAciklama:
      "Both devices had changed these records since the last sync. In each, the later change was kept:",
    buCihazdaki: "this device's",
    oburCihazdaki: "the other device's",
    cakismaSatiri: (tur: string, etiket: string, kazanan: string) => `${tur} “${etiket}” — ${kazanan} version kept`,
    elleDuzeltin: "If you need the lost change, correct that record by hand.",
    baglantiYenilenmeli: "Backup waiting — connection needs renewing",
    baglantiYenilenmeliMetin:
      "The browser needs a click to write to the cloud (permission or access period expired). Unwritten changes are kept in this browser.",
    sonYedekYazilamadi: "Last backup could not be written",
    yenidenDenenir: "It will be retried with the next change or with “Back up now”.",
    yedekleniyor: "Backing up…",
    degisiklikVar: "There are changes; they will be backed up in a few seconds.",
    yenidenBaglan: "Reconnect",
    hedefeYenidenBaglan: (ad: string) => `Reconnect to ${ad}`,
    simdiYedekle: "Back up now",
    depolamayiDegistir: "Change storage…",
    yalnizcaCihazaDon: "Back to this device only…",
    yeniYerAciklama:
      "Once connected to the new place, the vault's encrypted copy is written there right away. Parafon does not touch old files in your own cloud account.",
    baglanVeTasi: "Connect and move here",
    yeniBulutSaglayicisi: "New cloud provider",
    yalnizcaCihazKalsinMi: "Keep it only on this device?",
    yalnizcaCihazKalsinMiMetin:
      "Copies stop being written to the cloud. If site data is cleared or the device is lost, the vault comes back only from the encrypted backups you downloaded.",

    kuruluBulutYok: "No cloud is set up.",
    buluttakiDosyalar: "Files in the cloud",
    dosyalariListele: "List backup files",

    kasaKodunuGirin: "Enter the vault code.",
    kisaParola: (n: number) =>
      `For the Parafon server, the vault password must be at least ${n} characters. First lengthen your password in Settings › Vault lock › “Change password”, then come back here.`,
    kasaParolanizGirin: "Enter your vault password.",
    dogrulanamiyor: "The password cannot be verified in this build; reload the page.",
    parolaYanlis: "Wrong password: enter this vault's password.",
    kasaKodu: "Vault code",
    yeniKodOlustur: "Create new vault code",
    baskaCihazinKodu: "Enter the code of another device that uses this vault",
    kasaKoduIpucu: "Shown on the other device under Settings › Automatic cloud backup. You can leave out the dashes.",
    kasaParolaniz: "Your vault password",
    kasaParolanizIpucu: (n: number) =>
      `Must be at least ${n} characters. Someone who gets the vault code could download the encrypted file and try passwords on their own computer; a long password makes this practically impossible.`,
    kodAdres: (ay: number) =>
      `The code is only an address: the files open with your password or 24 words; the Parafon server cannot open them. The server does not know the code or you. A vault folder not opened from any device for ${ay} months is permanently deleted from the server — you cannot be notified, because the server does not know who you are.`,
    gizle: "Hide",
    goster: "Show",
    qrGizle: "Hide QR",
    qrGoster: "Show QR",
    kopyalandi: "Copied",
    kopyala: "Copy",
    baskaCihazdaAc: (ay: number) =>
      `To open this vault on another device, choose Settings › Automatic cloud backup › Parafon server there and enter this code. Write the code only on your own devices. A folder not opened from any device for ${ay} months is deleted.`,
    qrOkutun: "Scan it on the other device with “Scan QR”. The QR holds only the code; on its own it does not open the files.",
    kodBulunamadi: "Backup waiting — vault code not found on the server",
    kodBulunamadiMetin: (ay: number) =>
      `The code may have been renewed on another device, or the folder deleted because no device opened it for ${ay} months. Get the new code from the other device and enter it; unwritten changes are kept in this browser.`,
    yeniKasaKodu: "New vault code",
    kodHatali: "The vault code is incomplete or wrong.",
    koduKaydet: "Save code",
    koduYenile: "Renew code…",
    yenilensinMi: "Renew the vault code?",
    yenilensinMiMetin:
      "Renew it if someone else got the code. A new folder is opened, all backup files are moved there and the old folder is deleted from the server. You will need to enter the new code by hand on other devices that use this vault; until then their backup waits.",
    yenileniyor: "Renewing…",
    yenile: "Renew",

    pfaiYok: "There is no .pfai file at this destination.",
    yedekDosyasi: "Backup file",
    yedegiNeyleAcacaksiniz: "How will you open the backup",
    kasaninParolasi: "Password of the vault the backup came from",
    yedeginParolasi: "Backup password",
    eskiBicimIpucu: "For old-format files (with a separate backup password), the backup password from that time.",
    cozuluyor: "Decrypting…",
    bununlaDegistir: "Replace this device's vault with this one",
    geriYukle: "Restore",
    baskaKasaya: "This backup belongs to another vault",
    baskaKasayaMetin:
      "The file you chose is not a backup of this device's vault but of another vault. Two separate vaults are not merged; nothing changed.",
    yerineKoyabilirsiniz: "If you want to use that vault on this device, you can put it in place of this device's vault.",
    yerineKoy: "Put it in place of this device's vault…",

    kasaDegisti: "Vault replaced",
    degistiKilit:
      "From now on, that vault's PASSWORD and 24 WORDS are valid on this device. This device's old password and words no longer open anything.",
    degistiYedek:
      "If automatic cloud backup is on, it stays on; the new vault is written to its own named files, and the old vault's cloud backup stays as it is.",
    yeniKasayiAc: "Open the new vault",
    silinecek: "The vault on this device will be deleted",
    silinecekOn: "The vault on this device",
    silinecekSayi: (hesap: number, hareket: number) =>
      `${hesap} ${hesap === 1 ? "account" : "accounts"}, ${hareket} ${hareket === 1 ? "transaction" : "transactions"}`,
    silinecekSon:
      "will be removed from this device; the cloud vault you choose will take its place. The two vaults are not merged.",
    kilitDegisir:
      "This device's password and 24 words stop working; the incoming vault's password and 24 words become valid.",
    onceSaklayin:
      "First keep the old vault (recommended). The downloaded file opens with this device's current password or 24 words; it can be brought back the same way later.",
    eskiIndirildi: "Old vault downloaded ✓",
    eskiIndir: "Download old vault to a file",
    kaydetmedenDevam: "I am continuing without saving the old vault",
    getirilecekNerede: "Where is the backup of the vault to bring",
  },
};
